import React from 'react';
import { FileText, Monitor, ShoppingCart, Building2, Zap, Package, BookOpen, Cloud, Store, RefreshCw } from 'lucide-react';
import featureDte from '../images/generated-v2/feature-dte.png';
import featureMulti from '../images/generated-v2/feature-multiplatform.png';
import featureCompras from '../images/generated-v2/feature-compras.png';
import featureEmpresas from '../images/generated-v2/feature-multiempresa.png';
import featureLibros from '../images/generated-v2/feature-libros-iva.png';
import featureIcloud from '../images/generated-v2/feature-catalogos.png';
import featureInventory from '../images/generated-v2/feature-inventory.png';

const DTE_PILLS = [
  ['01', 'Factura'], ['03', 'CCF'],     ['04', 'NR'],  ['05', 'NC'],   ['06', 'ND'],
  ['07', 'Retención'],  ['08', 'LIQ'],  ['09', 'DOC'], ['11', 'EXP'],
  ['14', 'SE'],         ['15', 'DON'],
];

export default function Features() {
  return (
    <section id="caracteristicas" className="bg-white py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-4">Características</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-ink-950 mb-4 text-balance">
            Todo el ciclo tributario, en una app.
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed">
            Desde la emisión del DTE hasta compras, inventario, cobros y pagos.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* 11 DTE — large, featured (spans 2 rows on lg) */}
          <div className="lg:row-span-2 bg-gradient-to-br from-indigo-50 to-white rounded-3xl border border-indigo-100 overflow-hidden shadow-card-glow">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={featureDte} alt="" aria-hidden="true" className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <div className="w-10 h-10 rounded-xl bg-brand-600 flex items-center justify-center mb-4">
                <FileText size={20} strokeWidth={2.4} className="text-white" />
              </div>
              <h3 className="font-bold text-xl text-ink-950 mb-2">11 tipos de DTE</h3>
              <p className="text-ink-500 text-[14px] leading-relaxed mb-5">
                Factura, CCF, Notas de Crédito, Débito y Remisión, Retención, Liquidación, Documento Contable, Exportación, Sujeto Excluido y Donación. Todos firmados y sincronizados con Hacienda.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {DTE_PILLS.map(([code, name]) => (
                  <span key={code} className="text-[11px] font-mono font-medium bg-white border border-ink-200 text-ink-700 px-2 py-1 rounded">
                    {code} {name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Multi-platform */}
          <FeatureCard
            img={featureMulti}
            imgBg="bg-cyan-50"
            Icon={Monitor}
            iconBg="bg-cyan-100"
            iconColor="text-cyan-600"
            title="4 plataformas, un solo login"
            body="iOS, Android, Windows y macOS. Con el plan Pro+Sync sincronizás entre dispositivos: empezá en el celular, terminá en la compu."
          />

          {/* Compras */}
          <FeatureCard
            img={featureCompras}
            imgBg="bg-violet-50"
            Icon={ShoppingCart}
            iconBg="bg-violet-100"
            iconColor="text-violet-600"
            title="Compras + cobros y pagos"
            body="Importa tus facturas recibidas desde Gmail, escanea con OCR, o ingresa manualmente. Controla cuentas por cobrar y por pagar locales con vencimientos y abonos."
          />

          {/* Multi-empresa */}
          <FeatureCard
            img={featureEmpresas}
            imgBg="bg-amber-50"
            Icon={Building2}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            title="Multi-empresa"
            body="Administra varios emisores desde una sola cuenta. Cada empresa con su certificado y credenciales propias."
          />

          {/* Contingency (no image) */}
          <div className="bg-white rounded-3xl border border-ink-100 p-7 hover:border-ink-200 hover:shadow-lg transition-all">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-4">
              <Zap size={20} strokeWidth={2.4} className="text-rose-600" />
            </div>
            <h3 className="font-bold text-base text-ink-950 mb-2">Modo contingencia</h3>
            <p className="text-ink-500 text-[14px] leading-relaxed">
              Cuando Hacienda no responde, seguís facturando. La app guarda los DTEs y los procesa apenas se restablece la API.
            </p>
          </div>

          {/* POS (no image) */}
          <div className="bg-white rounded-3xl border border-ink-100 p-7 hover:border-ink-200 hover:shadow-lg transition-all">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
              <Store size={20} strokeWidth={2.4} className="text-indigo-600" />
            </div>
            <h3 className="font-bold text-base text-ink-950 mb-2">Punto de Venta (POS)</h3>
            <p className="text-ink-500 text-[14px] leading-relaxed">
              Ventas al mostrador con tickets no fiscales, cierre de caja X/Z e impresión térmica. El stock baja solo y consolidás el día en una sola factura o CCF.
            </p>
          </div>

          {/* Device sync — Pro+Sync (no image) */}
          <div className="bg-white rounded-3xl border border-ink-100 p-7 hover:border-ink-200 hover:shadow-lg transition-all">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
              <RefreshCw size={20} strokeWidth={2.4} className="text-violet-600" />
            </div>
            <h3 className="font-bold text-base text-ink-950 mb-2">Sync entre dispositivos (Pro+Sync)</h3>
            <p className="text-ink-500 text-[14px] leading-relaxed">
              Tus facturas, clientes, productos, compras y tickets del POS entre tu celular y tu compu, con la misma cuenta. Prueba gratis de 1 mes, sin tarjeta.
            </p>
          </div>

          {/* Inventory */}
          <FeatureCard
            img={featureInventory}
            imgBg="bg-emerald-50"
            Icon={Package}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            title="Inventario integrado"
            body="Stock, SKU, 15 unidades de medida, categorías y alertas de stock bajo. Sin app separada."
          />

          {/* Libros de IVA */}
          <FeatureCard
            img={featureLibros}
            imgBg="bg-sky-50"
            Icon={BookOpen}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            title="Libros de IVA (anexo F-07)"
            body="Los 5 CSV del anexo F-07 generados en 1 toque. Tu contador los carga directo a Hacienda."
          />

          {/* iCloud sync — free on all plans */}
          <FeatureCard
            img={featureIcloud}
            imgBg="bg-sky-50"
            Icon={Cloud}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            title="iCloud sync — gratis"
            body="Tu facturación se respalda en iCloud automáticamente. Incluido en todos los planes, también en Lite. iPhone, iPad y Mac sincronizados sin configurar nada."
          />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ img, imgBg, Icon, iconBg, iconColor, title, body }) {
  return (
    <div className="bg-white rounded-3xl border border-ink-100 overflow-hidden hover:border-ink-200 hover:shadow-lg transition-all">
      <div className={`aspect-[16/10] overflow-hidden ${imgBg}`}>
        <img src={img} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>
      <div className="p-7">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center mb-4`}>
          <Icon size={20} strokeWidth={2.4} className={iconColor} />
        </div>
        <h3 className="font-bold text-base text-ink-950 mb-2">{title}</h3>
        <p className="text-ink-500 text-[14px] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
